import React from "react";
import * as MUI from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, useForm } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import ConfirmAndCancelButtons from "../../../../components/buttons/ConfirmAndCancelButtons";
import useSubmit from "../../../master/species/hooks/useSubmit";
import usePut from "../../../../hooks/usePut";
import * as apiPaths from "../../../../enums/ApiPaths";
import * as keys from "../../../../enums/QueryKeys";
import { fetchInstitutions } from "../../../../enums/queryFunction/Fetch";
import SearchableAutocomplete from "../../clinic/components/warehouse/components/SearchableAutocomplete";
import { ANIMAL, MATERIAL } from "./ReferralType";

const LONG_FIELD_MAX_LENGTH = 2000;
const MATERIAL_MAX_LENGTH = 500;

export default function ReferralTemplateFormDialog({
  open,
  onClose,
  clinicId,
  initialValues,
  defaultReferralType = ANIMAL,
}) {
  const { t } = useTranslation();
  const theme = MUI.useTheme();
  const fullScreen = MUI.useMediaQuery(theme.breakpoints.down("md"));
  const isEditMode = Boolean(initialValues);

  const {
    formState: { errors },
    register,
    handleSubmit,
    control,
    watch,
  } = useForm({
    defaultValues: {
      title: initialValues?.title ?? "",
      referralType: initialValues?.referralType ?? defaultReferralType,
      diagnosis: initialValues?.diagnosis ?? "",
      requestedExam: initialValues?.requestedExam ?? "",
      material: initialValues?.material ?? "",
      numberOfSamples: initialValues?.numberOfSamples ?? "",
      institutionName: initialValues?.institution?.name ?? "",
    },
  });

  const referralType = watch("referralType");

  const { data: institutions } = useQuery({
    queryKey: [keys.INSTITUTIONS_LIST + clinicId],
    queryFn: () => fetchInstitutions(clinicId, ""),
  });
  const institutionOptions = Array.isArray(institutions)
    ? institutions.map((institution) => institution.name)
    : [];

  const templateUrl = `${apiPaths.REFERRAL_TEMPLATE}${clinicId}`;
  const create = useSubmit(null, templateUrl, null, [keys.REFERRAL_TEMPLATES_LIST + clinicId]);
  const edit = usePut(
    templateUrl,
    [keys.REFERRAL_TEMPLATES_LIST + clinicId],
    [keys.REFERRAL_TEMPLATES_LIST + clinicId],
  );

  const submitData = async (data) => {
    const isMaterial = data.referralType === MATERIAL;
    const payload = {
      ...data,
      material: isMaterial ? data.material : null,
      numberOfSamples: isMaterial && data.numberOfSamples ? Number(data.numberOfSamples) : null,
      institutionName: data.institutionName?.trim() || null,
    };
    const obj = isEditMode ? { ...initialValues, ...payload } : payload;
    const success = isEditMode
      ? await edit.submitCallback(obj)
      : await create.submitCallback(obj);
    if (success) {
      onClose();
    }
  };

  return (
    <MUI.Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 4 } }}
    >
      <MUI.Stack direction="row" justifyContent="flex-end" sx={{ p: 1 }}>
        <MUI.IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </MUI.IconButton>
      </MUI.Stack>
      <MUI.DialogContent
        sx={{
          p: { xs: 2.5, sm: 3.5 },
          pt: 0,
          bgcolor: (theme) =>
            theme.palette.mode === "light" ? "grey.50" : "rgba(255,255,255,0.02)",
        }}
      >
        <form onSubmit={handleSubmit(submitData)}>
          <MUI.Stack spacing={3}>
            <Controller
              control={control}
              name="referralType"
              render={({ field }) => (
                <MUI.ToggleButtonGroup
                  exclusive
                  size="small"
                  value={field.value}
                  onChange={(event, value) => value && field.onChange(value)}
                  sx={{ alignSelf: "flex-start" }}
                >
                  <MUI.ToggleButton value={ANIMAL} sx={{ textTransform: "none", px: 2 }}>
                    {t("ReferralForAnimal")}
                  </MUI.ToggleButton>
                  <MUI.ToggleButton value={MATERIAL} sx={{ textTransform: "none", px: 2 }}>
                    {t("ReferralForMaterial")}
                  </MUI.ToggleButton>
                </MUI.ToggleButtonGroup>
              )}
            />

            <MUI.Box>
              <Controller
                control={control}
                name="institutionName"
                render={({ field }) => (
                  <SearchableAutocomplete
                    options={institutionOptions}
                    value={field.value}
                    onChange={field.onChange}
                    label="Institution"
                    maxLength={120}
                  />
                )}
              />
            </MUI.Box>

            <MUI.Box>
              <MUI.Typography variant="subtitle1" gutterBottom sx={{ mb: 1.5, fontWeight: 500 }}>
                {t("Title")} *
              </MUI.Typography>
              <MUI.TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder={t("ReferralTemplateTitlePlaceholder")}
                inputProps={{ maxLength: 120 }}
                {...register("title", { required: t("EmptyField"), maxLength: { value: 120 } })}
                error={!!errors.title}
                helperText={errors.title?.message}
              />
            </MUI.Box>

            <MUI.Box>
              <MUI.Typography variant="subtitle1" gutterBottom sx={{ mb: 1.5, fontWeight: 500 }}>
                {t("Diagnosis")}
              </MUI.Typography>
              <MUI.TextField
                fullWidth
                multiline
                minRows={2}
                variant="outlined"
                size="small"
                placeholder={t("DiagnosisPlaceholder")}
                inputProps={{ maxLength: LONG_FIELD_MAX_LENGTH }}
                {...register("diagnosis")}
              />
            </MUI.Box>

            <MUI.Box>
              <MUI.Typography variant="subtitle1" gutterBottom sx={{ mb: 1.5, fontWeight: 500 }}>
                {t("RequestedExam")}
              </MUI.Typography>
              <MUI.TextField
                fullWidth
                multiline
                minRows={2}
                variant="outlined"
                size="small"
                placeholder={t("RequestedExamPlaceholder")}
                inputProps={{ maxLength: LONG_FIELD_MAX_LENGTH }}
                {...register("requestedExam")}
              />
            </MUI.Box>

            {referralType === MATERIAL && (
              <MUI.Box
                sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}
              >
                <MUI.Box sx={{ flex: 2 }}>
                  <MUI.Typography variant="subtitle1" gutterBottom sx={{ mb: 1.5, fontWeight: 500 }}>
                    {t("Material")}
                  </MUI.Typography>
                  <MUI.TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder={t("MaterialPlaceholder")}
                    inputProps={{ maxLength: MATERIAL_MAX_LENGTH }}
                    {...register("material")}
                  />
                </MUI.Box>
                <MUI.Box sx={{ flex: 1 }}>
                  <MUI.Typography variant="subtitle1" gutterBottom sx={{ mb: 1.5, fontWeight: 500 }}>
                    {t("NumberOfSamples")}
                  </MUI.Typography>
                  <MUI.TextField
                    fullWidth
                    type="number"
                    variant="outlined"
                    size="small"
                    placeholder="1"
                    inputProps={{ min: 0 }}
                    {...register("numberOfSamples")}
                  />
                </MUI.Box>
              </MUI.Box>
            )}

            <MUI.Divider />
            <MUI.Typography variant="caption" color="error">
              {`* ${t("RequiredFields")}`}
            </MUI.Typography>

            <ConfirmAndCancelButtons
              useCustomLoadingButtonOnConfirm={true}
              isConfirmLoading={isEditMode ? edit.loading : create.loading}
              confirmTitle="Save"
              cancelTitle=""
              confirmButtonType="submit"
            />
          </MUI.Stack>
        </form>
      </MUI.DialogContent>
    </MUI.Dialog>
  );
}
